import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, forkJoin, from } from 'rxjs';
import { map, catchError, switchMap, tap, filter, exhaustMap, withLatestFrom } from 'rxjs/operators';
import * as AuthActions from './auth.actions';
import { selectUserInfo } from './auth.selectors';
import { AuthService } from '../../services/auth.service';
import { SettingsService } from '../../services/settings.service';
import { StorageService } from '../../services/storage/storage.service';
import { resolvePostAuthRoute } from '../../auth/password-gate';
import { toast } from '../../../shared/utils/toast';

@Injectable()
export class AuthEffects {
  private actions$ = inject(Actions);
  private authService = inject(AuthService);
  private settingsService = inject(SettingsService);
  private router = inject(Router);
  private storageService = inject(StorageService);
  private store = inject(Store);

  rehydrateSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.sessionLoaded),
      filter((action) => action.session !== null),
      switchMap(({ session }) => {
        const sess = session!;
        const userId = sess.user.id;

        // Load cached info asynchronously
        return from(Promise.all([
          this.storageService.getItem<any>('userInfo'),
          this.storageService.getItem<any>('doctorInfo'),
          this.storageService.getItem<string[]>('permissions'),
          this.storageService.getItem<string>('userStatus')
        ])).pipe(
          switchMap(([cachedUserInfo, cachedDoctorInfo, cachedPermissions, cachedUserStatus]) => {
            // A cached userInfo missing branches_id/branch_id is stale (e.g. cached
            // before this account was assigned a branch) — trusting it blindly left
            // branchId null forever, since nothing else ever invalidates this cache
            // short of a fresh login. Require it to look complete before reusing it.
            const cachedUserInfoHasBranch = !!(cachedUserInfo?.branches_id || cachedUserInfo?.branch_id);
            if (cachedUserInfo && cachedUserInfoHasBranch && cachedDoctorInfo && cachedPermissions && (cachedUserStatus !== null && cachedUserStatus !== undefined)) {
              console.log('NGX Store: Rehydrating session from persistent cache');
              return of(AuthActions.loginSuccess({
                session: sess,
                userInfo: cachedUserInfo,
                doctorInfo: cachedDoctorInfo,
                permissions: cachedPermissions,
                userStatus: cachedUserStatus,
                isRehydration: true
              }));
            }
            
            console.log('NGX Store: No valid cache found, fetching user profile from API');
            // If not cached, fetch from API
            return forkJoin({
              userInfoRpc: from(this.authService.getUserInfo(userId)),
              userTableInfo: from(this.authService.queryUsersTable(userId)),
              doctorInfo: from(this.authService.getDoctorInfoByUuid(userId))
            }).pipe(
              switchMap(({ userInfoRpc, userTableInfo, doctorInfo }) => {
                const rpcData = Array.isArray(userInfoRpc?.data) ? userInfoRpc?.data[0] : userInfoRpc?.data;
                const tableData = Array.isArray(userTableInfo?.data) ? userTableInfo?.data[0] : userTableInfo?.data;
                
                const rawUserInfo = { ...rpcData, ...tableData };
                const rawDoctorInfo = Array.isArray(doctorInfo?.data) ? doctorInfo?.data[0] : doctorInfo?.data;
                
                const branchId = rawUserInfo?.branches_id || rawUserInfo?.branch_id;
                
                const loginAccess$ = branchId
                  ? from(this.authService.updateLoginAccess(branchId))
                  : of({ data: null, error: null });

                // The active clinic's own display settings (each clinic has its own; a clinic switch
                // reloads, so this always reads the clinic now in use).
                const clinic$ = branchId
                  ? this.settingsService.getClinicSettings(branchId).pipe(catchError(() => of(null)))
                  : of(null);

                return forkJoin({
                  loginAccess: loginAccess$,
                  permissions: from(this.authService.getPermissions(userId)),
                  clinic: clinic$
                }).pipe(
                  map(({ permissions, clinic }) => {
                    // Patient identifier shown (internal serial vs external_id)
                    if (rawUserInfo && clinic?.patient_identifier_type) {
                      rawUserInfo.patient_identifier_type = clinic.patient_identifier_type;
                    }
                    // Tooth notation (FDI vs Universal/USA) — display-only.
                    if (rawUserInfo && clinic?.tooth_notation) {
                      rawUserInfo.tooth_notation = clinic.tooth_notation;
                    }
                    // The switch for the CRM link in the sidebar footer.
                    // Tested against undefined rather than for truthiness like the
                    // two above, because this one is a BOOLEAN: `false` is the
                    // whole point of the setting and a truthy check would drop it.
                    if (rawUserInfo && clinic?.show_crm_link !== undefined) {
                      rawUserInfo.show_crm_link = clinic.show_crm_link;
                    }
                    const perms = permissions?.data?.map((p: any) => p.permissions).flat() || [];
                    const userStatus = permissions?.data?.[0]?.user_status?.toString() || '';

                    // Ensure we have a consistent name field
                    if (rawUserInfo && !rawUserInfo.full_name && rawUserInfo.user_name) {
                      rawUserInfo.full_name = rawUserInfo.user_name;
                    }
            

                    const isInactive = 
                      userStatus?.toLowerCase() === 'inativo' || 
                      rawUserInfo?.status?.toLowerCase() === 'inativo' || 
                      rawDoctorInfo?.status?.toLowerCase() === 'inativo' ||
                      rawDoctorInfo?.is_deleted === true;

                    if (isInactive) {
                      console.warn('User account is marked as INACTIVE. Triggering logout.');
                      // Boot-time toast: fires from APP_INITIALIZER before the locale file loads,
                      // so instant() would show the raw key - keep the literal.
                      toast.error('Account inactive. Contact support.', { id: 'auto-logout' });
                      this.authService.signOut();
                      return AuthActions.signOutSuccess();
                    }

                    return AuthActions.loginSuccess({
                      session: sess,
                      userInfo: rawUserInfo,
                      doctorInfo: rawDoctorInfo,
                      permissions: perms,
                      userStatus: userStatus,
                      isRehydration: true
                    });
                  })
                );
              }),
              catchError(err => {
                console.error('CRITICAL: Error rehydrating session. Triggering logout as safety measure.', err);
                // Boot-time toast: same loader race as above - keep the literal.
                toast.error('Session failed to load. Please try again.', { id: 'auto-logout' });
                this.authService.signOut();
                return of(AuthActions.loginFailure({ error: err.message || 'Error rehydrating session' }));
              })
            );
          })
        );
      })
    )
  );

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      switchMap(({ email, password }) =>
        from(this.authService.signIn(email, password)).pipe(
          switchMap(({ data, error }) => {
            if (error) {
              return of(AuthActions.loginFailure({ error: error.message }));
            }
            
            const session = data.session!;
            const userId = data.user!.id;

            return forkJoin({
              userInfoRpc: from(this.authService.getUserInfo(userId)),
              userTableInfo: from(this.authService.queryUsersTable(userId)),
              doctorInfo: from(this.authService.getDoctorInfoByUuid(userId))
            }).pipe(
              switchMap(({ userInfoRpc, userTableInfo, doctorInfo }) => {
                const rpcData = Array.isArray(userInfoRpc?.data) ? userInfoRpc?.data[0] : userInfoRpc?.data;
                const tableData = Array.isArray(userTableInfo?.data) ? userTableInfo?.data[0] : userTableInfo?.data;
                
                const rawUserInfo = { ...rpcData, ...tableData };
                const rawDoctorInfo = Array.isArray(doctorInfo?.data) ? doctorInfo?.data[0] : doctorInfo?.data;
                
                const branchId = rawUserInfo?.branches_id || rawUserInfo?.branch_id;
                
                const loginAccess$ = branchId
                  ? from(this.authService.updateLoginAccess(branchId))
                  : of({ data: null, error: null });

                // The active clinic's own display settings (each clinic has its own; a clinic switch
                // reloads, so this always reads the clinic now in use).
                const clinic$ = branchId
                  ? this.settingsService.getClinicSettings(branchId).pipe(catchError(() => of(null)))
                  : of(null);

                return forkJoin({
                  loginAccess: loginAccess$,
                  permissions: from(this.authService.getPermissions(userId)),
                  clinic: clinic$
                }).pipe(
                  map(({ permissions, clinic }) => {
                    // Patient identifier shown (internal serial vs external_id)
                    if (rawUserInfo && clinic?.patient_identifier_type) {
                      rawUserInfo.patient_identifier_type = clinic.patient_identifier_type;
                    }
                    // Tooth notation (FDI vs Universal/USA) — display-only.
                    if (rawUserInfo && clinic?.tooth_notation) {
                      rawUserInfo.tooth_notation = clinic.tooth_notation;
                    }
                    // The switch for the CRM link in the sidebar footer.
                    // Tested against undefined rather than for truthiness like the
                    // two above, because this one is a BOOLEAN: `false` is the
                    // whole point of the setting and a truthy check would drop it.
                    if (rawUserInfo && clinic?.show_crm_link !== undefined) {
                      rawUserInfo.show_crm_link = clinic.show_crm_link;
                    }
                    const perms = permissions?.data?.map((p: any) => p.permissions).flat() || [];
                    const userStatus = permissions?.data?.[0]?.user_status?.toString() || '';

                    // Ensure we have a consistent name field
                    if (rawUserInfo && !rawUserInfo.full_name && rawUserInfo.user_name) {
                      rawUserInfo.full_name = rawUserInfo.user_name;
                    }

                    const isInactive = 
                      userStatus?.toLowerCase() === 'inativo' || 
                      rawUserInfo?.status?.toLowerCase() === 'inativo' || 
                      rawDoctorInfo?.status?.toLowerCase() === 'inativo' ||
                      rawDoctorInfo?.is_deleted === true;

                    if (isInactive) {
                      this.authService.signOut();
                      return AuthActions.loginFailure({ error: 'USER_INACTIVE_ERROR' });
                    }

                    return AuthActions.loginSuccess({
                      session,
                      userInfo: rawUserInfo,
                      doctorInfo: rawDoctorInfo,
                      permissions: perms,
                      userStatus: userStatus,
                      isRehydration: false
                    });
                  })
                );
              }),
              catchError(err => of(AuthActions.loginFailure({ error: err.message || 'Error fetching user data' })))
            );
          }),
          catchError(err => of(AuthActions.loginFailure({ error: err.message || 'Login failed' })))
        )
      )
    )
  );

  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginSuccess),
      switchMap(({ session, userInfo, doctorInfo, permissions, userStatus, isRehydration }) => {
        // Keep AuthService state in sync for legacy components/services
        this.authService.setSession(session);
        this.authService.setUserProfile(userInfo);

        // Cache user info and permissions asynchronously
        const ops: Promise<void>[] = [];
        if (userInfo) ops.push(this.storageService.setItem('userInfo', userInfo));
        if (doctorInfo) ops.push(this.storageService.setItem('doctorInfo', doctorInfo));
        if (permissions) ops.push(this.storageService.setItem('permissions', permissions));
        if (userStatus !== null && userStatus !== undefined) ops.push(this.storageService.setItem('userStatus', userStatus));

        return from(Promise.all(ops)).pipe(
          map(() => {
            // Only navigate if we are on login or root, and NOT in a recovery flow
            const currentUrl = this.router.url;
            const isRecovery = window.location.hash.includes('type=recovery');
            
            if (!isRehydration && !isRecovery && (currentUrl === '/login' || currentUrl === '/' || currentUrl === '/criar-conta')) {
              // resolvePostAuthRoute, not resolveLandingRoute: an account still on
              // its emailed temporary password goes to the password screen. authGuard
              // enforces that anyway — doing it here just avoids a visible
              // /dashboard -> /create-password double hop.
              //
              // replaceUrl, or Back lands on /login while already signed in.
              this.router.navigate(
                [resolvePostAuthRoute(session, permissions, userStatus)],
                { replaceUrl: true },
              );
            }
            return { type: 'NO_ACTION' }; // Placeholder action
          })
        );
      })
    )
  );

  /**
   * Keep the cached userInfo in step with updateUserInfo (a Settings save of the
   * identifier, tooth notation or CRM link). The rehydrate fast path trusts the
   * cache, so without this a reload brought back the old values until the next
   * sign-in. Effects see the state after the reducer has applied the action.
   */
  persistUserInfo$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.updateUserInfo),
        withLatestFrom(this.store.select(selectUserInfo)),
        filter(([, userInfo]) => !!userInfo),
        switchMap(([, userInfo]) => from(this.storageService.setItem('userInfo', userInfo))),
      ),
    { dispatch: false },
  );

  /**
   * Keep AuthService's session mirror in step with the store. It is otherwise
   * only ever written by loginSuccess$, so after a password change the two would
   * silently diverge on the must_change_password flag.
   */
  sessionUpdated$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.sessionUpdated),
        tap(({ session }) => this.authService.setSession(session)),
      ),
    { dispatch: false },
  );

  signOut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.signOut),
      tap(() => {
        // Navigate immediately to prevent "Page Not Found" flicker during async cleanup
        this.router.navigate(['/login'], { replaceUrl: true });
      }),
      exhaustMap(() => {
        console.log('NGX Store: Handling signOut action. Clearing storage and tokens.');
        
        // Clearing storage returns a promise
        return from(this.storageService.clear()).pipe(
          switchMap(() => {
            // Also clear raw localStorage fields used by the login page
            localStorage.removeItem('savedEmail');
            localStorage.removeItem('rememberMe');
            
            this.authService.setSession(null);
            this.authService.setUserProfile(null);
            
            // Return a stream that leads to SignOutSuccess
            return from(this.authService.signOut()).pipe(
              map(() => {
                console.log('Supabase signOut successful');
                return AuthActions.signOutSuccess();
              }),
              catchError((err) => {
                console.error('Supabase signOut failed, but continuing with local logout', err);
                return of(AuthActions.signOutSuccess());
              })
            );
          })
        );
      })
    )
  );

  signOutSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.signOutSuccess),
      tap(() => {
        this.router.navigate(['/login']);
      })
    ),
    { dispatch: false }
  );
}
